/**
 * Product × Location cross-reference data
 * Used by /locations/[city]/[product] pages for SEO-optimized city+product landing pages.
 */

export interface ProductLocationData {
  productSlug: string;
  productNameNl: string;
  productNameEn: string;
  productNameFr: string;
  productNameEs: string;
  productNameTr: string;
  /** Category slug in the cakes system (for filtering signature cakes), or null if no direct match */
  cakeCategory: string | null;
  cities: string[];
}

export interface CityData {
  slug: string;
  nameNl: string;
  nameEn: string;
  nameFr: string;
  nameEs: string;
  nameTr: string;
  postalCode: string;
}

export const CROSS_CITIES: Record<string, CityData> = {
  vlaardingen: {
    slug: 'vlaardingen',
    nameNl: 'Vlaardingen',
    nameEn: 'Vlaardingen',
    nameFr: 'Vlaardingen',
    nameEs: 'Vlaardingen',
    nameTr: 'Vlaardingen',
    postalCode: '3136 EW',
  },
  rotterdam: {
    slug: 'rotterdam',
    nameNl: 'Rotterdam',
    nameEn: 'Rotterdam',
    nameFr: 'Rotterdam',
    nameEs: 'Rotterdam',
    nameTr: 'Rotterdam',
    postalCode: '3027 JT',
  },
};

export const PRODUCT_LOCATIONS: ProductLocationData[] = [
  {
    productSlug: 'bruidstaart',
    productNameNl: 'Bruidstaart',
    productNameEn: 'Wedding Cake',
    productNameFr: 'Gâteau de Mariage',
    productNameEs: 'Tarta de Boda',
    productNameTr: 'Düğün Pastası',
    cakeCategory: 'wedding-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'verjaardagstaart',
    productNameNl: 'Verjaardagstaart',
    productNameEn: 'Birthday Cake',
    productNameFr: "Gâteau d'Anniversaire",
    productNameEs: 'Tarta de Cumpleaños',
    productNameTr: 'Doğum Günü Pastası',
    cakeCategory: 'birthday-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'kindertaart',
    productNameNl: 'Kindertaart',
    productNameEn: "Children's Cake",
    productNameFr: 'Gâteau pour Enfants',
    productNameEs: 'Tarta Infantil',
    productNameTr: 'Çocuk Pastası',
    cakeCategory: 'birthday-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'gender-reveal-taart',
    productNameNl: 'Gender Reveal Taart',
    productNameEn: 'Gender Reveal Cake',
    productNameFr: 'Gâteau Gender Reveal',
    productNameEs: 'Tarta Gender Reveal',
    productNameTr: 'Cinsiyet Açıklama Pastası',
    cakeCategory: 'gender-reveal-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'babyshower-taart',
    productNameNl: 'Babyshower Taart',
    productNameEn: 'Baby Shower Cake',
    productNameFr: 'Gâteau Baby Shower',
    productNameEs: 'Tarta Baby Shower',
    productNameTr: 'Baby Shower Pastası',
    cakeCategory: null,
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'bedrijfstaart',
    productNameNl: 'Bedrijfstaart',
    productNameEn: 'Corporate Cake',
    productNameFr: "Gâteau d'Entreprise",
    productNameEs: 'Tarta Corporativa',
    productNameTr: 'Kurumsal Pasta',
    cakeCategory: 'corporate-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'cupcakes',
    productNameNl: 'Cupcakes',
    productNameEn: 'Cupcakes',
    productNameFr: 'Cupcakes',
    productNameEs: 'Cupcakes',
    productNameTr: 'Cupcake',
    cakeCategory: null,
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'taart-op-maat',
    productNameNl: 'Taart op Maat',
    productNameEn: 'Custom Cake',
    productNameFr: 'Gâteau sur Mesure',
    productNameEs: 'Tarta Personalizada',
    productNameTr: 'Özel Tasarım Pasta',
    cakeCategory: null,
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'halal-taart',
    productNameNl: 'Halal Taart',
    productNameEn: 'Halal Cake',
    productNameFr: 'Gâteau Halal',
    productNameEs: 'Tarta Halal',
    productNameTr: 'Helal Pasta',
    cakeCategory: null,
    cities: ['vlaardingen', 'rotterdam'],
  },
  {
    productSlug: 'cijfertaart',
    productNameNl: 'Cijfertaart',
    productNameEn: 'Number Cake',
    productNameFr: 'Gâteau Chiffre',
    productNameEs: 'Tarta de Números',
    productNameTr: 'Rakam Pastası',
    cakeCategory: 'birthday-cakes',
    cities: ['vlaardingen', 'rotterdam'],
  },
];

export const VALID_PRODUCTS = PRODUCT_LOCATIONS.map((p) => p.productSlug);
export const VALID_CROSS_CITIES = Object.keys(CROSS_CITIES);

/** Look up product data by slug */
export function getProductBySlug(slug: string): ProductLocationData | undefined {
  return PRODUCT_LOCATIONS.find((p) => p.productSlug === slug);
}

/** Look up city data by slug */
export function getCityBySlug(slug: string): CityData | undefined {
  return CROSS_CITIES[slug];
}

/** Get all valid city×product combinations for generateStaticParams */
export function getAllCityProductCombinations(): Array<{ city: string; product: string }> {
  return PRODUCT_LOCATIONS.flatMap((p) =>
    p.cities.map((city) => ({ city, product: p.productSlug }))
  );
}

/** All products that have a landing page for the given city (in declared order). */
export function getProductsForCity(citySlug: string): ProductLocationData[] {
  return PRODUCT_LOCATIONS.filter((p) => p.cities.includes(citySlug));
}

/** Resolve the localized product name for a locale, falling back to Dutch. */
export function getLocalizedProductName(product: ProductLocationData, locale: string): string {
  switch (locale) {
    case 'en':
      return product.productNameEn;
    case 'fr':
      return product.productNameFr;
    case 'es':
      return product.productNameEs;
    case 'tr':
      return product.productNameTr;
    default:
      return product.productNameNl;
  }
}

/** Resolve the localized city name for a locale, falling back to the slug. */
export function getLocalizedCityName(citySlug: string, locale: string): string {
  const city = CROSS_CITIES[citySlug];
  if (!city) return citySlug;
  switch (locale) {
    case 'en':
      return city.nameEn;
    case 'fr':
      return city.nameFr;
    case 'es':
      return city.nameEs;
    case 'tr':
      return city.nameTr;
    default:
      return city.nameNl;
  }
}
